import { ApplicationError } from './errors';

import type { MediaRole } from './media';

export const detectorIds = ['tinyFaceDetector', 'ssdMobilenetv1', 'mtcnn', 'tinyYolov2'] as const;

export type DetectorId = (typeof detectorIds)[number];
export type LandmarkModelId = 'faceLandmark68Net' | 'faceLandmark68TinyNet';
export type ModelId =
    DetectorId | LandmarkModelId | 'faceRecognitionNet' | 'faceExpressionNet' | 'ageGenderNet';
export type FeatureId = 'matching' | 'landmarks' | 'expressions' | 'ageAndGender';

export interface DetectorSettings {
    ssd: { minConfidence: number; maxResults: number };
    tiny: { inputSize: number; scoreThreshold: number };
    mtcnn: {
        minFaceSize: number;
        scaleFactor: number;
        maxNumScales: number;
        scoreThresholds: [number, number, number];
        useCustomScaleSteps: boolean;
        scaleSteps: string;
    };
}

export interface OutputSettings {
    drawOverlay: Record<MediaRole, boolean>;
    drawUnknownFaces: boolean;
    drawBoxes: boolean;
    drawScores: boolean;
    drawLabels: boolean;
    drawLandmarkLines: boolean;
    drawLandmarkPoints: boolean;
    boxColor: string;
    matchColor: string;
    unknownColor: string;
    landmarkLineColor: string;
    landmarkPointColor: string;
    lineWidth: number;
    pointSize: number;
    fontSize: number;
    expressionMinProbability: number;
}

export interface AnalysisConfig {
    detector: DetectorId;
    landmarkModel: LandmarkModelId;
    maxInputDimension: number;
    features: Record<FeatureId, boolean>;
    alignAuxiliaryModels: boolean;
    distanceThreshold: number;
    modelBaseUrl: string;
    detectorSettings: DetectorSettings;
    output: OutputSettings;
}

export interface ModelCatalogEntry {
    id: ModelId;
    category: 'detector' | 'landmarks' | 'recognition' | 'expression' | 'demographics';
    bundled: boolean;
}

export const modelCatalog: readonly ModelCatalogEntry[] = [
    { id: 'tinyFaceDetector', category: 'detector', bundled: true },
    { id: 'ssdMobilenetv1', category: 'detector', bundled: true },
    { id: 'mtcnn', category: 'detector', bundled: true },
    { id: 'tinyYolov2', category: 'detector', bundled: true },
    { id: 'faceLandmark68Net', category: 'landmarks', bundled: true },
    { id: 'faceLandmark68TinyNet', category: 'landmarks', bundled: true },
    { id: 'faceRecognitionNet', category: 'recognition', bundled: true },
    { id: 'faceExpressionNet', category: 'expression', bundled: true },
    { id: 'ageGenderNet', category: 'demographics', bundled: true },
] as const;

const configuredModelBaseUrl = import.meta.env.VITE_FACE_API_MODEL_URL?.trim();
export const defaultModelBaseUrl =
    configuredModelBaseUrl !== undefined && configuredModelBaseUrl.length > 0
        ? configuredModelBaseUrl
        : '/models';

export const initialConfig: AnalysisConfig = {
    detector: 'tinyFaceDetector',
    landmarkModel: 'faceLandmark68TinyNet',
    maxInputDimension: 640,
    features: { matching: true, landmarks: true, expressions: false, ageAndGender: false },
    alignAuxiliaryModels: true,
    distanceThreshold: 0.6,
    modelBaseUrl: defaultModelBaseUrl,
    detectorSettings: {
        ssd: { minConfidence: 0.5, maxResults: 100 },
        tiny: { inputSize: 416, scoreThreshold: 0.5 },
        mtcnn: {
            minFaceSize: 20,
            scaleFactor: 0.709,
            maxNumScales: 10,
            scoreThresholds: [0.6, 0.7, 0.7],
            useCustomScaleSteps: false,
            scaleSteps: '',
        },
    },
    output: {
        drawOverlay: { reference: true, haystack: true },
        drawUnknownFaces: true,
        drawBoxes: true,
        drawScores: true,
        drawLabels: true,
        drawLandmarkLines: true,
        drawLandmarkPoints: true,
        boxColor: '#69f6d2',
        matchColor: '#69f6d2',
        unknownColor: '#f5c66b',
        landmarkLineColor: '#68a8ff',
        landmarkPointColor: '#f5c66b',
        lineWidth: 2,
        pointSize: 2,
        fontSize: 16,
        expressionMinProbability: 0.05,
    },
};

export function featureNeedsLandmarks(config: AnalysisConfig): boolean {
    return (
        config.features.matching ||
        config.features.landmarks ||
        (config.alignAuxiliaryModels &&
            (config.features.expressions || config.features.ageAndGender))
    );
}

export function getRequiredModelIds(config: AnalysisConfig): string[] {
    const required = new Set<string>([config.detector]);

    if (featureNeedsLandmarks(config)) {
        required.add(config.landmarkModel);
    }
    if (config.features.matching) {
        required.add('faceRecognitionNet');
    }
    if (config.features.expressions) {
        required.add('faceExpressionNet');
    }
    if (config.features.ageAndGender) {
        required.add('ageGenderNet');
    }

    return [...required];
}

export function getFeatureDisabledReason(
    feature: FeatureId,
    config: AnalysisConfig
): string | undefined {
    if (feature === 'landmarks' && config.landmarkModel === 'faceLandmark68TinyNet') {
        return undefined;
    }

    return undefined;
}

export function isFaceDrawn(
    face: { match?: { isMatch: boolean } },
    role: MediaRole,
    output: OutputSettings
): boolean {
    if (!output.drawOverlay[role]) return false;
    return output.drawUnknownFaces || face.match === undefined || face.match.isMatch;
}

export function parseScaleSteps(value: string): number[] | undefined {
    const trimmed = value.trim();
    if (trimmed.length === 0) return undefined;

    const parsed = trimmed.split(',').map((item) => Number(item.trim()));
    if (parsed.some((item) => !Number.isFinite(item) || item <= 0)) {
        throw new ApplicationError('invalidScaleSteps');
    }
    return parsed;
}

export function getInferenceDimensions(
    width: number,
    height: number,
    maxInputDimension: number
): { width: number; height: number; scaleX: number; scaleY: number } {
    const largestDimension = Math.max(width, height);
    if (maxInputDimension <= 0 || largestDimension <= maxInputDimension) {
        return { width, height, scaleX: 1, scaleY: 1 };
    }
    const ratio = maxInputDimension / largestDimension;
    const inferenceWidth = Math.max(1, Math.round(width * ratio));
    const inferenceHeight = Math.max(1, Math.round(height * ratio));
    return {
        width: inferenceWidth,
        height: inferenceHeight,
        scaleX: width / inferenceWidth,
        scaleY: height / inferenceHeight,
    };
}
